/**
 * H3 overlay subscriptions.
 * Drives the hex grid + heat shading + click-to-select cell in both 2D and 3D modes,
 * and triggers a marker re-render when snap settings change so units relocate to hex centers.
 *
 * The effect re-runs on viewMode change because lifecycle replaces the map instance
 * asynchronously; we wait for the new map to be ready before attaching listeners.
 */

import { useEffect } from 'react';
import type { MapInstance, ViewMode } from '@/map/map-setup';
import { animateCotUpdate } from '@/map/markers';
import { updateTrackHistory } from '@/map/track-history';
import {
  cellsForBounds,
  cellsForCamera3D,
  densityByCell,
  cellAt,
  snapEvents,
} from '@/map/h3/h3-utils';
import {
  renderH3Overlay2D,
  highlightCell2D,
  clearH3Overlay2D,
} from '@/map/h3/h3-overlay-2d';
import {
  renderH3Overlay3D,
  highlightCell3D,
  clearH3Overlay3D,
} from '@/map/h3/h3-overlay-3d';
import { useH3Store } from '@/stores/h3-store';
import { useMapStore } from '@/stores/map-store';
import { useCotStore } from '@/stores/cot-store';

/** Wire H3 store + map viewport + CoT density into the overlay (2D + 3D) */
export function useH3Subscriptions(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  const viewMode = useMapStore((s) => s.viewMode);

  useEffect(() => {
    let cancelled = false;
    let detach: (() => void) | null = null;

    const computeFills = (heatVisible: boolean, resolution: number): Map<string, number> | undefined => {
      if (!heatVisible) return undefined;
      const events = useCotStore.getState().events;
      const density = densityByCell(events, resolution);
      const max = Math.max(1, ...density.values());
      const fills = new Map<string, number>();
      for (const [cell, count] of density) {
        fills.set(cell, 0.15 + 0.55 * (count / max));
      }
      return fills;
    };

    const renderOverlay = (): void => {
      const map = mapRef.current;
      if (!map) return;
      const { gridVisible, heatVisible, resolution } = useH3Store.getState();
      if (!gridVisible && !heatVisible) {
        clearH3Overlay2D();
        clearH3Overlay3D();
        useH3Store.getState().setOverCellLimit(false);
        return;
      }

      if (modeRef.current === '2d') {
        clearH3Overlay3D();
        const map2d = map as google.maps.Map;
        const bounds = map2d.getBounds();
        if (!bounds) return;
        const { cells, overLimit } = cellsForBounds(bounds, resolution);
        useH3Store.getState().setOverCellLimit(overLimit);
        renderH3Overlay2D(map2d, cells, { fills: computeFills(heatVisible, resolution) });
      } else {
        clearH3Overlay2D();
        const map3d = map as google.maps.maps3d.Map3DElement;
        const center = map3d.center as google.maps.LatLngAltitudeLiteral | undefined;
        const range = map3d.range;
        if (!center || range == null) return;
        const { cells, overLimit } = cellsForCamera3D(
          { lat: center.lat, lng: center.lng },
          range,
          resolution,
        );
        useH3Store.getState().setOverCellLimit(overLimit);
        renderH3Overlay3D(map3d, cells, { fills: computeFills(heatVisible, resolution) });
      }
    };

    const reRenderMarkersWithSnap = (): void => {
      const map = mapRef.current;
      if (!map) return;
      const { snapUnits, resolution } = useH3Store.getState();
      const { events, previousPositions } = useCotStore.getState();
      const next = snapUnits ? snapEvents(events, resolution) : events;
      animateCotUpdate(map, modeRef.current, next, previousPositions);
      updateTrackHistory(map, modeRef.current, next);
    };

    const handleClick = (lat: number, lng: number): void => {
      const map = mapRef.current;
      if (!map) return;
      const { resolution } = useH3Store.getState();
      const cell = cellAt(lat, lng, resolution);
      useH3Store.getState().setSelectedCell(cell);
      if (modeRef.current === '2d') {
        highlightCell2D(map as google.maps.Map, cell);
      } else {
        highlightCell3D(map as google.maps.maps3d.Map3DElement, cell);
      }
    };

    // Listeners that need re-attaching when the map instance swaps
    const attachMapListeners = (): void => {
      const map = mapRef.current;
      if (!map) return;
      if (modeRef.current === '2d') {
        const map2d = map as google.maps.Map;
        const idle = map2d.addListener('idle', renderOverlay);
        detach = () => idle.remove();
      } else {
        const map3d = map as google.maps.maps3d.Map3DElement;
        const onSteady = (e: Event): void => {
          if ((e as CustomEvent<boolean>).detail === true) renderOverlay();
        };
        map3d.addEventListener('gmp-steadychange', onSteady);
        detach = () => map3d.removeEventListener('gmp-steadychange', onSteady);
      }
      renderOverlay();
    };

    // Lifecycle creates the new map asynchronously after viewMode flips — wait for it
    const waitForMapReady = (): void => {
      if (cancelled) return;
      const ready = mapRef.current && modeRef.current === viewMode;
      if (!ready) {
        requestAnimationFrame(waitForMapReady);
        return;
      }
      attachMapListeners();
    };
    waitForMapReady();

    // Cross-mode store subscriptions (do not depend on a specific map instance)
    let prevOverlayKey = overlayKey();
    let prevSnapKey = snapKey();
    const unsubH3 = useH3Store.subscribe(() => {
      const o = overlayKey();
      if (o !== prevOverlayKey) {
        prevOverlayKey = o;
        renderOverlay();
      }
      const s = snapKey();
      if (s !== prevSnapKey) {
        prevSnapKey = s;
        reRenderMarkersWithSnap();
      }
    });

    let prevCotEvents = useCotStore.getState().events;
    const unsubCot = useCotStore.subscribe((state) => {
      if (state.events === prevCotEvents) return;
      prevCotEvents = state.events;
      if (useH3Store.getState().heatVisible) renderOverlay();
    });

    let prevClick = useMapStore.getState().lastClick;
    const unsubClick = useMapStore.subscribe((state) => {
      if (state.lastClick === prevClick) return;
      prevClick = state.lastClick;
      if (!state.lastClick) return;
      handleClick(state.lastClick.lat, state.lastClick.lng);
    });

    let prevSelected = useH3Store.getState().selectedCell;
    const unsubSel = useH3Store.subscribe((state) => {
      if (state.selectedCell === prevSelected) return;
      prevSelected = state.selectedCell;
      const map = mapRef.current;
      if (!map) return;
      if (modeRef.current === '2d') {
        highlightCell2D(map as google.maps.Map, state.selectedCell);
      } else {
        highlightCell3D(map as google.maps.maps3d.Map3DElement, state.selectedCell);
      }
    });

    return () => {
      cancelled = true;
      if (detach) detach();
      unsubH3();
      unsubCot();
      unsubClick();
      unsubSel();
      clearH3Overlay2D();
      clearH3Overlay3D();
    };
  }, [viewMode]);
}

function overlayKey(): string {
  const s = useH3Store.getState();
  return `${s.gridVisible}|${s.heatVisible}|${s.resolution}`;
}

function snapKey(): string {
  const s = useH3Store.getState();
  return `${s.snapUnits}|${s.resolution}`;
}
